<template>
  <div class="about">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">教室について</h1>
      </div>
    </div>
    
    <section class="section">
      <div class="container">
        <div v-if="isLoading" class="loading-state">
          ページ内容を読み込み中...
        </div>
        
        <div v-else>
          <div class="about-intro">
            <div class="about-image">
              <img 
                :src="pageContent.welcome_image || require('@/assets/class_room/c1.png')" 
                alt="英語教室"
              >
            </div>
            <div class="about-content">
              <h2>{{ pageContent.welcome_title || '皆の英語教室へようこそ' }}</h2>
              <div v-if="pageContent.welcome_content" v-html="formatContent(pageContent.welcome_content)"></div>
              <div v-else>
                <p>皆の英語教室は2020年に設立された、大阪市北区にある英語教室です。「楽しく、実践的に」をモットーに、子どもからシニアまで、幅広い年齢層の方々に英語を教えています。</p>
                <p>当教室では、英語を単なる教科や試験対策としてではなく、コミュニケーションのツールとして学ぶことを大切にしています。実際に使える英語力を身につけることを目指しています。</p>
              </div>
            </div>
          </div>
          
          <div class="about-mission">
            <h2 class="section-title">{{ pageContent.mission_title || '私たちの使命' }}</h2>
            <div class="mission-content">
              <div v-if="pageContent.mission_content" v-html="formatContent(pageContent.mission_content)"></div>
              <div v-else>
                <p>私たちの使命は、学習者が自信を持って英語を使えるようになることです。言語は人と人をつなぐ架け橋であり、新しい世界への扉を開くものです。</p>
                <p>年齢や目的に関わらず、一人ひとりに合った指導方法で、英語学習の楽しさを実感していただくことを目指しています。</p>
              </div>
            </div>
          </div>
          
          <div class="about-features">
            <h2 class="section-title">{{ pageContent.features_title || '皆の英語教室で学ぶ、6つのポイント' }}</h2>
            <div class="features-grid">
              <div 
                v-for="(feature, index) in displayFeatures" 
                :key="index" 
                class="feature-item"
              >
                <div class="feature-icon">
                  <i :class="feature.icon"></i>
                </div>
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
              </div>
            </div>
          </div>
          
          <div class="about-teacher">
            <h2 class="section-title">{{ pageContent.teacher_title || '講師紹介' }}</h2>
            <div class="teacher-profile">
              <div class="teacher-image">
                <img 
                  :src="pageContent.teacher_image || require('@/assets/class_room/c2.png')" 
                  alt="講師"
                >
              </div>
              <div class="teacher-info">
                <h3>{{ pageContent.teacher_name || 'イヴリン・テオ先生' }}</h3>
                <p class="teacher-title">{{ pageContent.teacher_subtitle || 'ITTT（International TEFL and TESOL Training）認定英語講師' }}</p>
                <div v-if="pageContent.teacher_description" v-html="formatContent(pageContent.teacher_description)"></div>
                <div v-else>
                  <p>イヴリン・テオ先生は、ITTT（International TEFL and TESOL Training）認定の英語講師であり、20年以上にわたり子どもから大人、高齢者まで幅広い年代の学習者を指導してきた経験豊富な先生です。</p>
                  <p>シンガポール国立大学にて免疫学の博士号を取得され、現在は同大学で免疫学の講師としても活躍されています。学術研究や教育活動はすべて英語で行われ、多数の学術論文や著書も英語で執筆されています。</p>
                  <p>長年にわたる専門的な経験を通じて培われた、実践的で体系的な指導法が教室に取り入れられています。効果的なコミュニケーション力と実生活での英語の活用を重視した授業が行われます。</p>
                  <p>授業は英語で全て行われ、言語浸透型の学習環境が提供されます。ただし、小さなお子様や初心者の方に対しては、必要に応じて日本語での簡単な補足説明が行われる場合もあります。</p>
                  <p>テオ先生は英語と日本語の両方を話され、穏やかで励ましのある指導スタイルが特徴です。生徒一人ひとりの自信を育てることを重視し、積極的な発話とリスニングを通して、英語の力を引き出します。</p>
                  <p>生徒の皆さんが英語を楽しみながら、確実に力をつけていけるよう、ATELO ACADEMYは全力でサポートしてまいります。</p>
                  <p>どうぞよろしくお願いいたします。</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="about-gallery">
            <h2 class="section-title">{{ pageContent.gallery_title || '教室の様子' }}</h2>
            <div class="gallery-grid">
              <div 
                v-for="(image, index) in displayGalleryImages" 
                :key="index" 
                class="gallery-item"
              >
                <img :src="image" :alt="`教室の様子${index + 1}`">
              </div>
            </div>
          </div>
          
          <div class="cta-section">
            <h2>まずはお気軽に体験レッスンから！</h2>
            <p>4レッスン 4950円〜</p>
            <router-link to="/contact" class="btn btn-large">お問い合わせ</router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';

export default {
  name: 'AboutPage',
  setup() {
    const isLoading = ref(true);
    const pageContent = ref({
      welcome_title: '',
      welcome_content: '',
      welcome_image: '',
      mission_title: '',
      mission_content: '',
      features_title: '',
      features: [],
      teacher_title: '',
      teacher_name: '',
      teacher_subtitle: '',
      teacher_description: '',
      teacher_image: '',
      gallery_title: '',
      gallery_images: []
    });

    // Default features if none are loaded from backend
    const defaultFeatures = [
      { icon: 'fas fa-graduation-cap', title: '国際資格を持つ講師が指導', description: 'ITTT認定英語講師、シンガポール国立大学 免疫学講師' },
      { icon: 'fas fa-users', title: '老若男女みんなが楽しめる', description: 'どんな年齢の方も楽しめる、実践的な学び' },
      { icon: 'fas fa-user-graduate', title: '一人一人に合ったカリキュラム', description: '子どもも、お年寄りも、それぞれに合った指導' },
      { icon: 'fas fa-award', title: '英検対策もバッチリ！', description: '段階別の実力に合わせた指導で合格への近道' },
      { icon: 'fas fa-gamepad', title: '楽しく、遊びながら学ぶ', description: 'ゲーム、ロールプレイ、ストーリーテリング' },
      { icon: 'fas fa-subway', title: '駅チカ！アクセスよし', description: '御堂筋線・谷町線どちらからも！中崎町駅から徒歩2分' }
    ];

    // Default gallery images
    const defaultGalleryImages = [
      require('@/assets/class_room/c3.jpg'),
      require('@/assets/class_room/c4.jpg'),
      require('@/assets/class_room/c5.jpg'),
      require('@/assets/class_room/c6.jpg')
    ];

    // Computed properties for display
    const displayFeatures = computed(() => {
      return pageContent.value.features && pageContent.value.features.length > 0 
        ? pageContent.value.features 
        : defaultFeatures;
    });

    const displayGalleryImages = computed(() => {
      const images = pageContent.value.gallery_images && pageContent.value.gallery_images.length > 0 
        ? pageContent.value.gallery_images.filter(img => img && img.trim() !== '')
        : defaultGalleryImages;
      
      // If no valid images from backend, use defaults
      return images.length > 0 ? images : defaultGalleryImages;
    });

    // Load page content from backend
    const loadPageContent = async () => {
      try {
        const response = await fetch('https://enen.saitsuki.uk/api/page-content/about');
        
        if (response.ok) {
          const data = await response.json();
          const content = data.data;
          
          // Update page content with backend data
          pageContent.value = {
            welcome_title: content.welcome_title || '',
            welcome_content: content.welcome_content || '',
            welcome_image: content.welcome_image || '',
            mission_title: content.mission_title || '',
            mission_content: content.mission_content || '',
            features_title: content.features_title || '',
            features: content.features ? JSON.parse(content.features) : [],
            teacher_title: content.teacher_title || '',
            teacher_name: content.teacher_name || '',
            teacher_subtitle: content.teacher_subtitle || '',
            teacher_description: content.teacher_description || '',
            teacher_image: content.teacher_image || '',
            gallery_title: content.gallery_title || '',
            gallery_images: content.gallery_images ? JSON.parse(content.gallery_images) : []
          };
        }
      } catch (error) {
        console.error('ページ内容の読み込みに失敗しました:', error);
        // Continue with default content if loading fails
      } finally {
        isLoading.value = false;
      }
    };

    // Format content to handle line breaks
    const formatContent = (content) => {
      if (!content) return '';
      return content.replace(/\n/g, '<br>');
    };

    onMounted(() => {
      loadPageContent();
    });

    return {
      isLoading,
      pageContent,
      displayFeatures,
      displayGalleryImages,
      formatContent
    };
  }
};
</script>

<style scoped>
.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 60px 0;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: bold;
}

.about-intro {
  display: flex;
  margin: 50px 0;
  align-items: center;
}

.about-image {
  flex: 1;
  padding-right: 50px;
}

.about-image img {
  width: 100%;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.about-content {
  flex: 1;
}

.about-content h2 {
  font-size: 28px;
  margin-bottom: 20px;
  color: var(--primary-color);
}

.about-content p {
  margin-bottom: 15px;
  line-height: 1.6;
  color: #555;
}

.about-mission {
  background-color: var(--light-bg);
  padding: 50px;
  border-radius: 10px;
  margin-bottom: 60px;
}

.mission-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.mission-content p {
  margin-bottom: 15px;
  line-height: 1.6;
  font-size: 18px;
  color: #555;
}

.about-features {
  margin-bottom: 60px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 30px;
  margin-top: 40px;
}

.feature-item {
  background-color: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-icon {
  font-size: 28px;
  margin-bottom: 15px;
  color: white;
  display: inline-block;
  width: 60px;
  height: 60px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(255, 143, 171, 0.3);
}

.feature-item h3 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #333;
}

.feature-item p {
  color: #666;
  line-height: 1.5;
}

.about-teacher {
  margin-bottom: 60px;
}

.teacher-profile {
  display: flex;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.teacher-image {
  width: 35%;
  min-width: 300px;
  background-color: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.teacher-image img {
  width: 100%;
  height: auto;
  border-radius: 10px;
}

.teacher-info {
  flex: 1;
  padding: 30px;
}

.teacher-info h3 {
  font-size: 24px;
  color: var(--primary-color);
  margin-bottom: 5px;
}

.teacher-title {
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
}

.teacher-info p {
  margin-bottom: 15px;
  line-height: 1.6;
}

.about-gallery {
  margin-bottom: 60px;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
}

.gallery-item {
  height: 250px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item:hover img {
  transform: scale(1.05);
}

.cta-section {
  text-align: center;
  padding: 50px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: white;
  border-radius: 10px;
}

.cta-section h2 {
  font-size: 24px;
  margin-bottom: 15px;
}

.cta-section p {
  font-size: 18px;
  margin-bottom: 25px;
}

.btn-large {
  padding: 12px 30px;
  font-size: 18px;
  background-color: white;
  color: var(--primary-color);
}

.btn-large:hover {
  background-color: var(--accent-color);
}

@media (max-width: 900px) {
  .about-intro {
    flex-direction: column;
  }
  
  .about-image {
    width: 100%;
    padding-right: 0;
    margin-bottom: 30px;
  }
  
  .teacher-profile {
    flex-direction: column;
  }
  
  .teacher-image {
    width: 100%;
    min-width: auto;
  }
  
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
}
</style> 